/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  FileText, Zap, GripVertical, Grid3X3,
  Palette, Shirt, Users, Eye, Briefcase,
  CalendarDays, ClipboardList, MapPin,
  MessageSquare, FolderOpen, BarChart2, GitBranch, Share2,
  LogOut, User, Menu, X, Upload, Check, Pencil,
  ChevronDown, Layers, Package, Map, Sparkles,
  Boxes,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NotificationBell from './NotificationBell';
import { FiPrIcon } from './FiPrLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Navigation structure — 4 premium production sections ──────────────────────
const SECTIONS = [
  {
    id: 'workspace',
    label: 'WORKSPACE',
    Icon: Layers,
    collapsible: true,
    items: [
      { label: 'Script',      Icon: FileText,     path: 'script',      testid: 'nav-script' },
      { label: 'Breakdown',   Icon: Zap,           path: 'breakdown',   testid: 'nav-navigator' },
      { label: 'Beat Board',  Icon: Grid3X3,       path: 'beatboard',   testid: 'nav-beatboard' },
      { label: 'Strip Board', Icon: GripVertical,  path: 'stripboard',  testid: 'nav-stripboard' },
    ],
  },
  {
    id: 'departments',
    label: 'DEPARTMENTS',
    Icon: Package,
    collapsible: true,
    items: [
      { label: 'Art',         Icon: Palette,    path: 'dept/art',        testid: 'nav-art',        color: '#a78bfa', dept: 'ART' },
      { label: 'Casting',     Icon: Users,      path: 'dept/casting',    testid: 'nav-casting',    color: '#60a5fa', dept: 'CHARACTER' },
      { label: 'Costume',     Icon: Shirt,      path: 'dept/costume',    testid: 'nav-costume',    color: '#facc15', dept: 'COSTUME' },
      { label: 'Continuity',  Icon: Grid3X3,    path: 'continuity',      testid: 'nav-continuity2',color: '#fb7185', dept: 'CONTINUITY' },
      { label: 'Production',  Icon: Briefcase,  path: 'dept/production', testid: 'nav-production', color: '#4ade80', dept: 'PRODUCTION' },
      { label: 'VFX',         Icon: Eye,        path: 'dept/vfx',        testid: 'nav-vfx',        color: '#22d3ee', dept: 'VFX' },
    ],
  },
  {
    id: 'plan',
    label: 'PLAN',
    Icon: Map,
    collapsible: true,
    items: [
      { label: 'Schedule',      Icon: CalendarDays, path: 'schedule',   testid: 'nav-schedule' },
      { label: 'Call Sheets',   Icon: ClipboardList,path: 'callsheet',  testid: 'nav-callsheet' },
      { label: 'Locations',     Icon: MapPin,       path: 'breakdown',  testid: 'nav-locations' },
    ],
  },
  {
    // 2026-02 Phase 1 nav restructure: promoted from previous "Tasks" sub-item
    // under the old "SLATE" section. Same route ('slate'), same component
    // (SlatePage.jsx wrapping TaskPanel.jsx) — label + tier changed only.
    // 2026-02 Phase 1.1: Team + Share moved in from OTHERS.
    id: 'slate',
    label: 'SLATE',
    Icon: MessageSquare,
    collapsible: true,
    items: [
      { label: 'Slate', Icon: MessageSquare, path: 'slate', testid: 'nav-tasks', isSlate: true },
      { label: 'Team',  Icon: Users,         path: 'users', testid: 'nav-team' },
      { label: 'Share', Icon: Share2,        path: 'share', testid: 'nav-share' },
    ],
  },
  {
    // 2026-02 Phase 1 nav restructure: renamed from "SLATE" to "OTHERS"
    // (the Tasks sub-item was promoted to its own top-level section above).
    // 2026-02 Phase 1.1: Team + Share moved out to SLATE.
    id: 'others',
    label: 'OTHERS',
    Icon: Boxes,
    collapsible: true,
    items: [
      { label: 'Files & Media',Icon: FolderOpen,  path: 'versions', testid: 'nav-files' },
      { label: 'Reports',      Icon: BarChart2,   path: 'activity', testid: 'nav-reports' },
      { label: 'Versions',     Icon: GitBranch,   path: 'versions', testid: 'nav-versions' },
    ],
  },
];

// Simple color from string (for project avatar gradient)
const strColor = (s = '') => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue},55%,38%)`;
};

const ProjectLayout = () => {
  const { user, logout, token } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { projectId } = useParams();

  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [projectTitle,     setProjectTitle]      = useState('');
  const [renaming,         setRenaming]          = useState(false);
  const [renameText,       setRenameText]        = useState('');
  const [sidebarCollapsed, setSidebarCollapsed]  = useState(false);
  const [sidebarHovered,   setSidebarHovered]    = useState(false);
  const [slateUnread,      setSlateUnread]       = useState(0);
  const [deptsOpen,        setDeptsOpen]         = useState(true);
  // Track open/closed state per section.
  // Default: all COLLAPSED. Persist per-user in localStorage. On first visit,
  // any section owning the current active route auto-expands so the user
  // sees their current context.
  const _sectionsFromStorage = () => {
    try {
      const raw = localStorage.getItem('cb.projectSidebar.sections');
      if (raw) return JSON.parse(raw);
    } catch { /* no-op */ }
    return null;
  };
  const _initialSectionsOpen = () => {
    const stored = _sectionsFromStorage();
    if (stored) return stored;
    // First-time visit: everything collapsed, then auto-expand the section
    // that owns the current path so the user has orientation.
    // 2026-02: added 'others' (renamed from old 'slate') and re-added 'slate'
    // as the new promoted top-level section for the Slate/Tasks module.
    const base = { workspace: false, departments: false, plan: false, slate: false, others: false };
    try {
      const path = window.location.pathname || '';
      for (const sec of SECTIONS) {
        if (sec.items && sec.items.some(it => path.includes(`/${it.path}`))) {
          base[sec.id] = true;
        }
      }
    } catch { /* no-op */ }
    return base;
  };
  const [sectionsOpen, setSectionsOpen] = useState(_initialSectionsOpen);

  // Persist any section-open change.
  useEffect(() => {
    try { localStorage.setItem('cb.projectSidebar.sections', JSON.stringify(sectionsOpen)); }
    catch { /* no-op */ }
  }, [sectionsOpen]);
  const toggleSection = (id) =>
    setSectionsOpen(s => ({ ...s, [id]: !s[id] }));
  const [deptStats,        setDeptStats]         = useState({});   // { dept: count }
  const [sceneStats,       setSceneStats]        = useState({ total: 0, ready: 0 });
  const [hoveredItem,      setHoveredItem]       = useState(null);

  const showFull = !sidebarCollapsed || sidebarHovered;

  // ── Load project + stats ───────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId || !token) return;
    axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const p = r.data.find(p => p.id === projectId);
        if (p) setProjectTitle(p.title);
      }).catch(() => {});

    // Entity stats per dept
    axios.get(`${API}/projects/${projectId}/entities`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const counts = {};
        (r.data || []).forEach(e => { counts[e.type] = (counts[e.type] || 0) + 1; });
        setDeptStats(counts);
      }).catch(() => {});

    // Scene count
    axios.get(`${API}/projects/${projectId}/breakdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const scenes = r.data || [];
        const ready  = scenes.filter(s => s.scene_text_status === 'gemini_vision' || s.scene_text_status === 'ready').length;
        setSceneStats({ total: scenes.length, ready });
      }).catch(() => {});
  }, [projectId, token]);

  // ── Slate unread polling ───────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId || !token) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/slate/notifications`,
          { headers: { Authorization: `Bearer ${token}` } });
        setSlateUnread(res.data.total_unread || 0);
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [projectId, token]);

  useEffect(() => { setHoveredItem(null); }, [location.pathname]);

  const isActive = useCallback((path) =>
    location.pathname.includes(`/projects/${projectId}/${path}`)
  , [location.pathname, projectId]);

  const go = useCallback((path) => {
    navigate(`/projects/${projectId}/${path}`);
    setMobileOpen(false);
    setSidebarCollapsed(true);
    setHoveredItem(null);
  }, [navigate, projectId]);

  const handleSaveRename = async () => {
    const newTitle = renameText.trim();
    if (!newTitle || newTitle === projectTitle) { setRenaming(false); return; }
    try {
      await axios.patch(`${API}/projects/${projectId}`, { title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } });
      setProjectTitle(newTitle);
    } finally { setRenaming(false); }
  };

  const progressPct = sceneStats.total > 0
    ? Math.round((sceneStats.ready / sceneStats.total) * 100) : 0;

  // ── Section header ─────────────────────────────────────────────────────────
  const SectionHeader = ({ section }) => {
    const isOpen = sectionsOpen[section.id];

    if (!showFull) {
      // Collapsed sidebar: show a small section icon button (tap to toggle)
      return (
        <button
          onClick={() => toggleSection(section.id)}
          title={section.label}
          className="w-full flex items-center justify-center h-7 my-1 transition-all duration-150 rounded-md"
          style={{
            color: isOpen ? 'var(--cb-accent)' : 'var(--cb-text-dim)',
            background: isOpen ? 'color-mix(in srgb, var(--cb-accent) 8%, transparent)' : 'transparent',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--cb-accent)'}
          onMouseLeave={e => e.currentTarget.style.color = isOpen ? 'var(--cb-accent)' : 'var(--cb-text-dim)'}
        >
          <section.Icon className="h-3.5 w-3.5" strokeWidth={isOpen ? 2.5 : 1.5} />
        </button>
      );
    }

    return (
      <div className="mt-5 mb-1 px-3">
        <div className="flex items-center gap-2 cursor-pointer select-none"
             onClick={() => toggleSection(section.id)}>
          <section.Icon className="h-3 w-3 shrink-0" style={{ color: 'var(--cb-text-dim)' }} strokeWidth={2} />
          <span className="flex-1 text-[9px] font-black tracking-[0.18em] uppercase"
                style={{ color: 'var(--cb-text-dim)' }}>
            {section.label}
          </span>
          <ChevronDown
            className="h-3 w-3 transition-transform duration-200"
            style={{ color: 'var(--cb-text-dim)', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            strokeWidth={2.5}
          />
        </div>
        <div className="mt-1.5 h-px" style={{ background: 'var(--cb-border)' }} />
      </div>
    );
  };

  // ── Nav item ───────────────────────────────────────────────────────────────
  const NavItem = ({ item, isDept = false }) => {
    const active = isActive(item.path);
    const itemColor = item.color || 'var(--cb-text-sec)';

    if (!showFull) {
      return (
        <button
          className="relative w-full flex items-center justify-center h-8 rounded-md transition-all duration-150"
          style={{
            color: active ? (item.color || 'var(--cb-accent)') : 'var(--cb-text-dim)',
            background: active ? 'var(--cb-surface-h)' : 'transparent',
          }}
          onMouseEnter={e => {
            setHoveredItem({ path: item.path, label: item.label, color: item.color, top: e.currentTarget.getBoundingClientRect().top });
          }}
          onMouseLeave={() => setHoveredItem(null)}
          onClick={() => go(item.path)}
          data-testid={item.testid}
        >
          {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r"
                           style={{ background: item.color || 'var(--cb-accent)' }} />}
          <item.Icon className="h-4 w-4" strokeWidth={1.5} />
        </button>
      );
    }

    // Dept items — show colored dot + completion count
    if (isDept) {
      const count = deptStats[item.dept] || 0;
      return (
        <button
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 group relative"
          style={{
            color: active ? itemColor : 'var(--cb-text-sec)',
            background: active ? `${item.color}12` : 'transparent',
          }}
          onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--cb-surface)'; }}
          onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
          onClick={() => go(item.path)}
          data-testid={item.testid}
        >
          {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r" style={{ background: item.color }} />}
          {/* Color dot */}
          <span className="h-2 w-2 rounded-full shrink-0 transition-all duration-150"
                style={{ background: item.color, opacity: active ? 1 : 0.55, boxShadow: active ? `0 0 6px ${item.color}80` : 'none' }} />
          <item.Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
          <span className="flex-1 font-medium text-[13px] text-left">{item.label}</span>
          {count > 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0"
                  style={{ background: `${item.color}20`, color: item.color }}>
              {count}
            </span>
          )}
        </button>
      );
    }

    // Regular nav item
    return (
      <button
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 relative"
        style={{
          color: active ? 'var(--cb-accent)' : 'var(--cb-text-sec)',
          background: active ? 'color-mix(in srgb, var(--cb-accent) 10%, transparent)' : 'transparent',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--cb-surface)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
        onClick={() => go(item.path)}
        data-testid={item.testid}
      >
        {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r bg-[var(--cb-accent)]" />}
        <item.Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
        <span className="flex-1 font-medium text-[13px] text-left">{item.label}</span>
        {item.isSlate && slateUnread > 0 && (
          <span className="text-[9px] bg-red-500 text-white rounded-full px-1.5 py-0.5 font-bold shrink-0" data-testid="slate-sidebar-badge">
            {slateUnread > 99 ? '99+' : slateUnread}
          </span>
        )}
      </button>
    );
  };

  // ── Sidebar content ────────────────────────────────────────────────────────
  const SidebarContent = () => (
    <div className="flex flex-col h-full">

      {/* ── Logo + Back ── */}
      <div className="px-3 py-3 flex items-center justify-between shrink-0"
           style={{ borderBottom: '1px solid var(--cb-border)' }}>
        <button
          onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
          className="flex items-center gap-2 transition-opacity duration-150 hover:opacity-70"
          data-testid="back-to-dashboard"
          title="Dashboard"
        >
          <FiPrIcon size={showFull ? 26 : 28} />
          {showFull && (
            <span className="text-base font-bold tracking-tight" style={{ color: 'var(--cb-text)' }}>
              FiPr<span style={{ color: 'var(--cb-accent)' }}>.ai</span>
            </span>
          )}
        </button>
        {showFull && (
          <Button variant="ghost" size="sm"
            onClick={() => setMobileOpen(false)} className="md:hidden h-6 w-6 p-0"
            style={{ color: 'var(--cb-text-dim)' }}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* ── Project card ── */}
      {showFull ? (
        <div className="mx-3 mt-4 mb-1 rounded-xl p-3 shrink-0"
             style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
          <div className="flex items-center gap-2.5 mb-2.5">
            {/* Project avatar — gradient chip from project title */}
            <div className="h-9 w-9 rounded-lg shrink-0 flex items-center justify-center text-white font-black text-base select-none"
                 style={{ background: `linear-gradient(135deg, ${strColor(projectTitle)} 0%, ${strColor(projectTitle + '1')} 100%)` }}>
              {(projectTitle || 'P')[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              {renaming ? (
                <div className="flex items-center gap-1">
                  <input autoFocus value={renameText}
                    onChange={e => setRenameText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveRename(); if (e.key === 'Escape') setRenaming(false); }}
                    className="text-sm font-semibold bg-transparent border-b outline-none flex-1 min-w-0"
                    style={{ color: 'var(--cb-text)', borderColor: 'var(--cb-accent)' }}
                    data-testid="project-rename-input"
                  />
                  <button onClick={handleSaveRename}
                    className="h-5 w-5 rounded flex items-center justify-center shrink-0"
                    style={{ background: 'var(--cb-accent)' }} data-testid="project-rename-save">
                    <Check className="h-3 w-3 text-black" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 group/rename">
                  <span className="text-sm font-semibold truncate leading-tight" style={{ color: 'var(--cb-text)' }}>
                    {projectTitle || 'Project'}
                  </span>
                  <button
                    onClick={() => { setRenameText(projectTitle); setRenaming(true); }}
                    className="opacity-0 group-hover/rename:opacity-100 transition-opacity"
                    title="Rename project" data-testid="project-rename-btn">
                    <Pencil className="h-3 w-3" strokeWidth={1.5} style={{ color: 'var(--cb-text-dim)' }} />
                  </button>
                </div>
              )}
              <span className="text-[10px]" style={{ color: 'var(--cb-text-dim)' }}>
                {sceneStats.total} scenes
              </span>
            </div>
          </div>

          {/* Production progress bar */}
          {sceneStats.total > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: 'var(--cb-text-dim)' }}>
                  Script Coverage
                </span>
                <span className="text-[9px] font-bold" style={{ color: progressPct > 0 ? 'var(--cb-accent)' : 'var(--cb-text-dim)' }}>
                  {progressPct}%
                </span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ background: 'var(--cb-border)' }}>
                <div className="h-full rounded-full transition-all duration-500"
                     style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, var(--cb-accent) 0%, #e0961b 100%)' }} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-center mt-3 shrink-0">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-black text-sm"
               style={{ background: `linear-gradient(135deg, ${strColor(projectTitle)} 0%, ${strColor(projectTitle + '1')} 100%)` }}>
            {(projectTitle || 'P')[0].toUpperCase()}
          </div>
        </div>
      )}

      {/* ── Upload Script button ── */}
      <div className={`${showFull ? 'px-3' : 'px-2'} mt-3 mb-1 shrink-0`}>
        {showFull ? (
          <button
            onClick={() => { navigate(`/projects/${projectId}/upload?new=1`); setMobileOpen(false); setSidebarCollapsed(true); }}
            className="w-full flex items-center justify-center gap-1.5 h-8 text-xs font-semibold rounded-lg transition-all duration-200"
            style={{ border: '1px dashed color-mix(in srgb, var(--cb-accent) 35%, transparent)', color: 'var(--cb-accent)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--cb-accent) 8%, transparent)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            data-testid="nav-upload-new-script"
          >
            <Upload className="h-3 w-3" strokeWidth={2} /> Upload Script
          </button>
        ) : (
          <button
            onClick={() => { navigate(`/projects/${projectId}/upload?new=1`); setMobileOpen(false); setSidebarCollapsed(true); }}
            className="w-full flex items-center justify-center h-8 rounded-lg transition-colors duration-150"
            style={{ color: 'var(--cb-accent)', opacity: 0.6 }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
            title="Upload Script"
          >
            <Upload className="h-4 w-4" strokeWidth={2} />
          </button>
        )}
      </div>

      {/* ── Nav sections ── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden pb-2 px-1.5">
        {SECTIONS.map(section => (
          <div key={section.id}>
            <SectionHeader section={section} />

            {/* Items — respect sectionsOpen in both expanded & collapsed views */}
            <div className="overflow-hidden transition-all duration-200"
                 style={{
                   maxHeight: sectionsOpen[section.id] ? '500px' : '0px',
                   opacity:   sectionsOpen[section.id] ? 1 : 0,
                 }}>
              {section.items.map(item => (
                <NavItem key={item.path + item.label} item={item}
                         isDept={section.id === 'departments'} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* ── User footer ── */}
      <div className="shrink-0 px-2 py-3" style={{ borderTop: '1px solid var(--cb-border)' }}>
        {showFull ? (
          <div className="px-1 mb-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: 'color-mix(in srgb, var(--cb-accent) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--cb-accent) 20%, transparent)' }}>
                <span className="text-[11px] font-bold" style={{ color: 'var(--cb-accent)' }}>
                  {user?.full_name?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate" style={{ color: 'var(--cb-text)' }}>{user?.full_name}</div>
                <div className="text-[10px]" style={{ color: 'var(--cb-text-dim)' }}>{user?.role}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-2 pl-9">
              <button onClick={() => { navigate('/dashboard/profile'); setMobileOpen(false); }}
                className="text-[10px] transition-colors"
                style={{ color: 'var(--cb-text-dim)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--cb-accent)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--cb-text-dim)'}
                data-testid="nav-profile-link">
                Settings
              </button>
              <span style={{ color: 'var(--cb-border)' }}>·</span>
              <button onClick={() => { navigate('/dashboard/themes'); setMobileOpen(false); }}
                className="text-[10px] transition-colors flex items-center gap-0.5"
                style={{ color: 'var(--cb-text-dim)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--cb-accent)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--cb-text-dim)'}
                data-testid="nav-themes-link">
                <Sparkles className="h-2.5 w-2.5" strokeWidth={1.5} /> Themes
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => { navigate('/dashboard/profile'); setMobileOpen(false); }}
            className="w-full flex items-center justify-center h-8 transition-colors"
            style={{ color: 'var(--cb-text-dim)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--cb-accent)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--cb-text-dim)'}
            title="Profile" data-testid="nav-profile-link">
            <User className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
        {showFull ? (
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="w-full flex items-center justify-center gap-1.5 h-7 text-xs rounded-lg transition-all duration-200 font-medium"
            style={{ color: 'var(--cb-text-dim)', border: '1px solid var(--cb-border)' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = '#f8717140'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--cb-text-dim)'; e.currentTarget.style.borderColor = 'var(--cb-border)'; }}
            data-testid="logout-btn">
            <LogOut className="h-3 w-3" strokeWidth={1.5} /> Logout
          </button>
        ) : (
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="w-full flex items-center justify-center h-8 transition-colors"
            style={{ color: 'var(--cb-text-dim)' }}
            onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--cb-text-dim)'}
            title="Logout" data-testid="logout-btn">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--cb-bg)' }}>
      {/* Mobile hamburger */}
      <Button variant="ghost" size="sm"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 h-8 w-8 p-0"
        style={{ color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
        data-testid="open-sidebar-btn">
        <Menu className="h-4 w-4" strokeWidth={1.5} />
      </Button>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/70 z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={`flex flex-col fixed inset-y-0 left-0 z-50 transform transition-all duration-200 md:relative md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${showFull ? 'w-56' : 'w-14'}`}
        style={{ background: 'var(--cb-sidebar-bg)', borderRight: '1px solid var(--cb-sidebar-bdr)' }}
        data-testid="project-sidebar"
        onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
        onMouseLeave={() => { setSidebarHovered(false); setHoveredItem(null); }}
      >
        {/* Collapse toggle (desktop only) */}
        <button
          className="hidden md:flex absolute -right-3 top-20 h-6 w-6 rounded-full items-center justify-center z-10 transition-opacity opacity-0 hover:opacity-100 group-hover:opacity-100"
          style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', color: 'var(--cb-text-dim)' }}
          onClick={() => { setSidebarCollapsed(c => !c); setSidebarHovered(false); }}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronDown className="h-3 w-3" style={{ transform: sidebarCollapsed ? 'rotate(-90deg)' : 'rotate(90deg)' }} strokeWidth={2.5} />
        </button>

        <SidebarContent />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto min-w-0 relative" style={{ background: 'var(--cb-bg)' }}>
        <NotificationBell projectId={projectId} />
        <Outlet />
      </div>

      {/* Collapsed hover tooltip */}
      {sidebarCollapsed && !sidebarHovered && hoveredItem && (
        <div
          className="hidden md:block fixed left-[60px] z-[60] px-3 py-1.5 rounded-lg text-xs font-semibold pointer-events-none whitespace-nowrap"
          style={{
            top: hoveredItem.top + 4,
            background: 'var(--cb-surface)',
            border: '1px solid var(--cb-border)',
            color: hoveredItem.color || 'var(--cb-text)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          }}
          data-testid="sidebar-hover-label"
        >
          {hoveredItem.label}
        </div>
      )}
    </div>
  );
};

export default ProjectLayout;
